import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheoryProgress } from '../../hooks/useTheoryProgress';
import { useVideoProgress } from '../../hooks/useVideoProgress';

const Arrays: React.FC = () => {
  const navigate = useNavigate();
  useTheoryProgress('arrays');
  const videoRef = useVideoProgress('arrays');

  const handleBackClick = () => {
    navigate('/topics');
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Minimal grid background */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:64px_64px] [background-position-x:center]"></div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Back to Topics</span>
          </motion.button>
        </div>

        {/* Title and description */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-normal text-white mb-2">Arrays</h1>
          <p className="text-white/70 text-lg">Master array data structures and their common operations and patterns.</p>
        </motion.div>

        <div className="space-y-12">
          {/* Introduction */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">What Are Arrays?</h2>
            <div className="text-white/70 space-y-4">
              <p>
                Arrays are fundamental data structures that store elements in contiguous memory locations, 
                enabling fast access via indices. They're like a row of lockers—each has a number, and you can quickly open any locker by its position.
              </p>
              <p>
                Arrays are versatile and widely used due to their simplicity and efficiency for sequential data storage and retrieval.
              </p>
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mt-6">
                <h3 className="text-xl font-semibold text-white mb-3">Core Characteristics</h3>
                <ul className="list-disc list-inside text-white/70 space-y-2">
                  <li><strong>Contiguous Memory:</strong> Elements stored sequentially</li>
                  <li><strong>Fixed Size:</strong> Size often set at creation (in static languages)</li>
                  <li><strong>Indexed Access:</strong> Direct access via position</li>
                  <li><strong>Homogeneous:</strong> Typically same-type elements</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* Basic Operations */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Basic Operations</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">Core Manipulations</h3>
                <div className="text-2xl">📦</div>
              </div>
              <p className="text-white/70 mb-4">
                Arrays support fundamental operations for managing elements efficiently.
              </p>
              <div className="bg-white/5 p-4 rounded-lg">
                <pre className="text-sm text-white/80 font-mono">
                  <code>{`// Creating arrays
const numbers = [1, 2, 3, 4, 5];
const empty = new Array(5); // Length 5, undefined elements

// Accessing elements
const first = numbers[0]; // O(1)
const last = numbers[numbers.length - 1];

// Modifying elements
numbers[2] = 10; // O(1)

// Adding elements
numbers.push(6);    // O(1) amortized
numbers.unshift(0); // O(n) due to shifting

// Removing elements
numbers.pop();      // O(1)
numbers.shift();    // O(n) due to shifting`}</code>
                </pre>
              </div>
            </div>
          </motion.section>

          {/* Array Methods */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Array Methods</h2>
            <div className="space-y-6">
              {/* Transformation Methods */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Transformation Methods</h3>
                  <div className="text-2xl">🔧</div>
                </div>
                <p className="text-white/70 mb-4">
                  Methods that transform or create new arrays from existing ones.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`const numbers = [1, 2, 3, 4, 5];

// Map: Transform each element
const doubled = numbers.map(x => x * 2); // [2, 4, 6, 8, 10]

// Filter: Keep elements passing test
const evens = numbers.filter(x => x % 2 === 0); // [2, 4]

// Reduce: Aggregate elements
const sum = numbers.reduce((acc, curr) => acc + curr, 0); // 15`}</code>
                  </pre>
                </div>
              </div>

              {/* Search Methods */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Search Methods</h3>
                  <div className="text-2xl">🔍</div>
                </div>
                <p className="text-white/70 mb-4">
                  Methods to locate elements or verify their presence.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`const numbers = [1, 2, 3, 4, 5];

// Find: First matching element
const found = numbers.find(x => x > 3); // 4

// Includes: Check existence
const hasFive = numbers.includes(5); // true

// IndexOf: Get position
const index = numbers.indexOf(3); // 2`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Common Algorithms */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Common Algorithms</h2>
            <div className="space-y-6">
              {/* Reversing an Array */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Reversing an Array</h3>
                  <div className="text-2xl">🔄</div>
                </div>
                <p className="text-white/70 mb-4">
                  In-place reversal using two pointers to swap elements.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`function reverseArray(arr: number[]): number[] {
  let left = 0;
  let right = arr.length - 1;
  
  while (left < right) {
    [arr[left], arr[right]] = [arr[right], arr[left]];
   [left++;
    right--;
  }
  return arr;
}

const nums = [1, 2, 3, 4, 5];
reverseArray(nums); // [5, 4, 3, 2, 1]`}</code>
                  </pre>
                </div>
              </div>

              {/* Finding Maximum Subarray */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Maximum Subarray Sum</h3>
                  <div className="text-2xl">📈</div>
                </div>
                <p className="text-white/70 mb-4">
                  Kadane's algorithm to find the largest sum of a contiguous subarray.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`function maxSubarray(arr: number[]): number {
  let maxSum = arr[0];
  let currentSum = arr[0];
  
  for (let i = 1; i < arr.length; i++) {
    currentSum = Math.max(arr[i], currentSum + arr[i]);
    maxSum = Math.max(maxSum, currentSum);
  }
  return maxSum;
}

const nums = [-2, 1, -3, 4, -1, 2, 1];
maxSubarray(nums); // 6 (subarray [4, -1, 2, 1])`}</code>
                  </pre>
                </div>
              </div>

              {/* Two-Pointer Technique */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Two-Pointer Technique</h3>
                  <div className="text-2xl">👈👉</div>
                </div>
                <p className="text-white/70 mb-4">
                  Efficiently finds pairs summing to a target using two pointers.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`function findPair(arr: number[], target: number): [number, number] | null {
  let left = 0;
  let right = arr.length - 1;
  
  while (left < right) {
    const sum = arr[left] + arr[right];
    if (sum === target) return [arr[left], arr[right]];
    if (sum < target) left++;
    else right--;
  }
  return null;
}

const sortedArr = [2, 4, 6, 8, 10];
findPair(sortedArr, 14); // [4, 10]`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Time Complexity */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Time Complexity</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                Understanding operation costs helps optimize array usage:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-white/70">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-3 px-4 text-left">Operation</th>
                      <th className="py-3 px-4 text-left">Time Complexity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Access by Index</td>
                      <td className="py-3 px-4">O(1)</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Search (Linear)</td>
                      <td className="py-3 px-4">O(n)</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Insert/Delete at End</td>
                      <td className="py-3 px-4">O(1) amortized</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Insert/Delete at Start/Middle</td>
                      <td className="py-3 px-4">O(n)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.section>

          {/* Best Practices */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Best Practices</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                Tips for effective array usage:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Do's</h3>
                  <ul className="list-disc list-inside text-white/70 space-y-2">
                    <li>Leverage built-in methods for efficiency</li>
                    <li>Pre-allocate size when possible</li>
                    <li>Validate indices to prevent errors</li>
                    <li>Use typed arrays for performance</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Don'ts</h3>
                  <ul className="list-disc list-inside text-white/70 space-y-2">
                    <li>Avoid frequent resizing</li>
                    <li>Don't overuse shift/unshift</li>
                    <li>Avoid sparse arrays</li>
                    <li>Don't mix incompatible types</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Video Tutorial */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Video Tutorial</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <video
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  controls
                  preload="metadata"
                  ref={videoRef}
                >
                  <source src="https://files.catbox.moe/nuy48a.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <p className="text-white/70 mt-4 text-center">
                Watch this video for a visual understanding of arrays and their operations.
              </p>
            </div>
          </motion.section>

          {/* Why It Matters */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Why Arrays Matter</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <ul className="space-y-4 text-white/70">
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Speed:</strong> O(1) access makes them highly efficient.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Simplicity:</strong> Intuitive structure for sequential data.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Foundation:</strong> Basis for many algorithms and data structures.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Versatility:</strong> Supports a wide range of operations.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Next Steps */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Next Steps</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                With array basics mastered, you can:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-2">
                <li>Implement array-based algorithms</li>
                <li>Optimize operations for performance</li>
                <li>Explore multidimensional arrays</li>
                <li>Practice real-world applications</li>
              </ul>
              <div className="mt-6 flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors"
                  onClick={() => navigate('/practice/arrays')}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Start Practice</span>
                </motion.button>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default Arrays;